import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { obsAPI } from '../api';

const MODES = [
  { k: 'alone',   l: 'Observing alone' },
  { k: 'lead',    l: 'With a co-observer' },
  { k: 'joining', l: 'Joining someone' },
];

/** Said in every mode — it is the reason the notes are separate. */
function PrivacyNote() {
  return (
    <div className="co-privacy">
      <strong>Notes stay private during the lesson.</strong> That is deliberate.
      If you can see each other's notes while observing you end up recording the
      same things, and the point of co-observing is to find out whether you both
      saw the same student learning.
    </div>
  );
}

/**
 * Any observation can become a co-observation.
 *
 * The lead shares a session code; co-observers join on their own device and
 * record independently. Whoever started the session leads it — they run the
 * debrief and send the feedback.
 */
export default function CoObservation({
  mode, onModeChange,          // 'alone' | 'lead' | 'joining'
  sessionCode,                 // the reserved code, once there is one
  onCodeReserved,              // called with a freshly reserved code
  observers = [],              // who has joined so far
  onJoined,                    // called with the observation when joining works
}) {
  const [code, setCode]   = useState('');
  const [joining, setJoining] = useState(false);
  const [reserving, setReserving] = useState(false);

  // The code is reserved as soon as the lead chooses to co-observe, so it can
  // be shared straight away — and held, so nobody else is given the same one
  // before this observation starts. Observing alone reserves nothing.
  useEffect(() => {
    if (mode !== 'lead' || sessionCode) return;
    let cancelled = false;
    setReserving(true);
    obsAPI.reserveCode()
      .then(({ data }) => { if (!cancelled) onCodeReserved?.(data.session_code); })
      .catch(err => {
        console.error(err);
        if (!cancelled) toast.error('Could not get a session code. Please try again.');
      })
      .finally(() => { if (!cancelled) setReserving(false); });
    return () => { cancelled = true; };
  }, [mode, sessionCode, onCodeReserved]);

  const join = async () => {
    const entered = code.trim().toUpperCase();
    if (!entered) { toast.error('Enter the code the lead observer gave you.'); return; }
    setJoining(true);
    try {
      const { data } = await obsAPI.join(entered);
      toast.success('Joined. Your notes are your own until the lesson ends.');
      onJoined?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not join with that code.');
    } finally { setJoining(false); }
  };

  const others = observers.filter(o => !o.is_you);

  return (
    <div className="co-block">
      <div className="co-modes">
        {MODES.map(m => (
          <button key={m.k}
                  className={`co-mode${mode === m.k ? ' on' : ''}`}
                  onClick={() => onModeChange(m.k)}>
            {m.l}
          </button>
        ))}
      </div>

      {mode === 'lead' && (
        <div className="co-body">
          {sessionCode ? (
            <>
              <div className="co-lbl">Your session code</div>
              <div className="co-code">{sessionCode}</div>
              <div className="co-hint">
                Share this with anyone joining you. You are the lead, so you run the
                debrief and send the feedback. Everyone who joins gets a copy of the record.
              </div>

              <div className="co-joined">
                {others.length === 0
                  ? 'No one has joined yet.'
                  : others.map(o => (
                      <div key={o.id} className="co-person">
                        <span className="co-av">{o.initials}</span>
                        <span>{o.name}</span>
                      </div>
                    ))}
              </div>
              <div className="co-hint">
                Anyone with the code who is allowed to observe in this school can join,
                once you have started the observation.
              </div>
            </>
          ) : (
            <div className="co-hint">
              {reserving ? 'Getting your session code…' : 'No session code yet.'}
            </div>
          )}
          <PrivacyNote />
        </div>
      )}

      {mode === 'joining' && (
        <div className="co-body">
          <div className="co-lbl">Session code from the lead observer</div>
          <div className="co-join-row">
            <input className="fi co-code-input" value={code} placeholder="ELO-0000"
                   onChange={e => setCode(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && join()} />
            <button className="co-join-btn" onClick={join} disabled={joining}>
              {joining ? '…' : 'Join'}
            </button>
          </div>
          <div className="co-hint">Enter the code the lead observer gave you.</div>
          <PrivacyNote />
        </div>
      )}

      {mode === 'alone' && (
        <div className="co-body"><PrivacyNote /></div>
      )}
    </div>
  );
}
